import { useState, type ReactNode } from "react";
import { Link2 } from "lucide-react";
import { ActionModal, Button, DialogCancel, Field, Icon, Sheet } from "@cntrl/ui";
import { usePhoneLayout } from "../../app/phoneLayout.js";
import { useRelease } from "../../app/hooks/useRelease.js";
import { usePushTaskMedia, type PushTaskMediaResult, type TaskMediaDto } from "../../app/hooks/useTaskMedia.js";
import { errorMessage } from "../../app/hooks/apiError.js";
import { MediaImage } from "../releases/MediaImage.js";
import { LINK_SLOTS, SLOT_LABELS, SLOT_ORDER, mediaMeta, slotsForMedia, typeLabel, type MediaSlot } from "./taskMedia.js";
import styles from "./PushMediaDialog.module.scss";

/** What is being pushed: one of the task's uploads, or one of its links. */
export type PushSource =
  | { kind: "upload"; media: TaskMediaDto }
  | { kind: "link"; url: string; label: string };

/**
 * The PUSH picker (ART-368): send one task media item into a release asset slot. Lists only the slots that take
 * this kind of item, in the Assets tab's order; a slot that already holds something says it will be replaced (the
 * server keeps the old file in Files as "{slot} (previous)"). A modal at desktop widths, a bottom sheet on a phone.
 */
export function PushMediaDialog({
  taskId,
  releaseId,
  releaseTitle,
  source,
  onClose,
  onPushed,
}: {
  taskId: string;
  releaseId: string;
  releaseTitle: string;
  source: PushSource;
  onClose: () => void;
  onPushed: (result: PushTaskMediaResult, slotLabel: string) => void;
}) {
  const phone = usePhoneLayout();
  const push = usePushTaskMedia();
  const release = useRelease(releaseId);
  const assets = release.data?.data?.assets?.items ?? [];

  const fits: readonly MediaSlot[] = source.kind === "upload" ? slotsForMedia(source.media.kind) : LINK_SLOTS;
  const slots = SLOT_ORDER.filter((s) => fits.includes(s));
  const name = source.kind === "upload" ? source.media.fileName || "File" : source.label;
  const meta = source.kind === "upload" ? mediaMeta(source.media) : hostOf(source.url);

  const [slot, setSlot] = useState<MediaSlot | null>(null);
  const [label, setLabel] = useState(() => withoutExtension(name));
  const [error, setError] = useState<string | null>(null);

  /** A canonical slot is "taken" when its tile already holds a file or a link — the tile the Assets tab draws. */
  function takes(s: MediaSlot): boolean {
    if (s === "file") return false;
    const tile = assets.find((a) => a.kind === s);
    return Boolean(tile && tile.status === "added" && (tile.mediaId || tile.url));
  }

  async function confirm() {
    if (!slot) return;
    setError(null);
    try {
      const result = await push.mutateAsync({
        taskId,
        body: {
          slot,
          taskMediaId: source.kind === "upload" ? source.media.taskMediaId : null,
          linkUrl: source.kind === "link" ? source.url : null,
          label: slot === "file" ? label.trim() || null : null,
        },
      });
      onPushed(result, SLOT_LABELS[slot]);
    } catch (e) {
      setError(errorMessage(e, "Couldn't push it"));
    }
  }

  const title = `Push to ${releaseTitle}`;
  const body: ReactNode = (
    <div className={styles.body} data-sheet={phone || undefined}>
      <div className={styles.head}>
        <span className={styles.thumb} aria-hidden="true">
          {source.kind === "upload" && source.media.kind === "image" ? (
            <MediaImage mediaId={source.media.mediaId} alt="" className={styles.thumbImg} />
          ) : source.kind === "upload" ? (
            <span className={styles.typeTile}>{typeLabel(source.media.fileName, source.media.kind)}</span>
          ) : (
            <Icon icon={Link2} size={20} />
          )}
        </span>
        <span className={styles.headText}>
          <span className={styles.headTitle}>{title}</span>
          <span className={styles.headSub}>{meta ? `${name} · ${meta}` : name}</span>
        </span>
      </div>

      <p className={styles.label} id={`push-slots-${taskId}`}>Send to</p>
      <div className={styles.slots} role="radiogroup" aria-labelledby={`push-slots-${taskId}`}>
        {slots.map((s) => (
          <label key={s} className={styles.slot} data-selected={slot === s || undefined}>
            <input
              type="radio"
              name={`push-slot-${taskId}`}
              className={styles.radio}
              checked={slot === s}
              onChange={() => setSlot(s)}
            />
            <span className={styles.slotName}>{SLOT_LABELS[s]}</span>
            {s === "file" && <span className={styles.slotHint}>label it</span>}
            {takes(s) && <span className={styles.replaces}>Replaces current</span>}
          </label>
        ))}
      </div>
      {slot === "file" && (
        <Field label="File label" value={label} maxLength={200} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Alt art" />
      )}
      {phone && <p className={styles.helper}>Only slots that fit this file are shown.</p>}
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );

  if (!phone) {
    return (
      <ActionModal
        title={title}
        hideHeader
        confirmLabel="Push"
        onConfirm={() => void confirm()}
        onCancel={onClose}
        loading={push.isPending}
        confirmDisabled={!slot}
      >
        {body}
      </ActionModal>
    );
  }

  return (
    <Sheet title={title} onClose={onClose} stacked>
      {body}
      <div className={styles.footer}>
        <DialogCancel disabled={push.isPending} />
        <Button onClick={() => void confirm()} disabled={!slot} loading={push.isPending}>
          Push
        </Button>
      </div>
    </Sheet>
  );
}

function withoutExtension(name: string): string {
  const i = name.lastIndexOf(".");
  return i > 0 ? name.slice(0, i) : name;
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}
